"use client";

import type { Node as ProseMirrorNode } from "prosemirror-model";
import { EditorState, Plugin } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { ImageMetadata } from "@/utils/media-utils";
import { IGNORE_SAME_ORIGIN_LINK_CLICK_ATTRIBUTE } from "@/utils/navigation-events";

import { EDITOR_MODE } from "./constants";
import { isEditorBranchVisible } from "./editor-dom-focus";
import {
  docHasUnresolvedInlineImageUpload,
  markdownHasLocalBlobImage,
} from "./inline-image-upload-state";
import {
  parseMarkdownToDoc,
  serializeDocToMarkdown,
} from "./markdown/markdown";
import {
  parseMarkdownSourceToDoc,
  serializeMarkdownSourceDoc,
} from "./markdown/source-doc";
import { createCodeBlockNodeView } from "./node-views/code-block-node-view";
import { createImageNodeViewFactory } from "./node-views/image-node-view";
import { buildEditorPlugins } from "./plugins";
import type { EditorSuggestionState } from "./plugins/suggestions";
import {
  closeEditorSuggestion,
  editorSuggestionsKey,
} from "./plugins/suggestions";
import { focusSchema } from "./schema";
import {
  EditorSuggestionMenu,
  type SuggestionControls,
} from "./suggestion-menu";
import { uploadInlineImageFile } from "./upload/inline-image-upload";

const docHasContent = (doc: ProseMirrorNode) =>
  !(
    doc.childCount === 1 &&
    doc.firstChild?.type.name === "paragraph" &&
    doc.firstChild.content.size === 0
  );

const parseValueToDoc = (value: string, mode: EDITOR_MODE) =>
  mode === EDITOR_MODE.SIMPLE
    ? parseMarkdownSourceToDoc(value)
    : parseMarkdownToDoc(value);

const serializeDocForMode = (doc: ProseMirrorNode, mode: EDITOR_MODE) =>
  mode === EDITOR_MODE.SIMPLE
    ? serializeMarkdownSourceDoc(doc)
    : serializeDocToMarkdown(doc);

const normalizeBoundaryValue = (value: string) => value.trim();

const rememberLocalBoundaryValue = (values: Set<string>, value: string) => {
  values.add(normalizeBoundaryValue(value));

  if (values.size <= 20) {
    return;
  }

  const [oldest] = values;

  if (oldest != null) {
    values.delete(oldest);
  }
};

export type ProseMirrorEditorProps = {
  autoFocus: boolean;
  className?: string;
  mode: EDITOR_MODE;
  name: string;
  placeholder: string;
  setEditorInstance: (instance: {
    getter: () => EditorView;
    type: "prosemirror";
  }) => void;
  setEditorValue: (value: string) => void;
  setFocusedField: (field: string | null) => void;
  setImmediateTextContent: (hasContent: boolean) => void;
  setInlineImageUploading: (uploading: boolean) => void;
  setLastFocusedField: (field: string) => void;
  updateInlineImageMetadata: (
    updater: (
      previous: Record<string, ImageMetadata>,
    ) => Record<string, ImageMetadata>,
  ) => void;
  uploadImageFile?: (file: File) => Promise<string | null>;
  value: string;
};

export const ProseMirrorEditor = ({
  autoFocus,
  className = "",
  mode,
  name,
  placeholder,
  setEditorInstance,
  setEditorValue,
  setFocusedField,
  setImmediateTextContent,
  setInlineImageUploading,
  setLastFocusedField,
  updateInlineImageMetadata,
  uploadImageFile = uploadInlineImageFile,
  value,
}: ProseMirrorEditorProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const debounceTimeout = useRef<NodeJS.Timeout>(null);
  const localBoundaryValuesRef = useRef<Set<string>>(new Set());
  const modeRef = useRef(mode);
  const suggestionControlsRef = useRef<SuggestionControls | null>(null);
  const [focused, setFocused] = useState(false);
  const [hasDocumentContent, setHasDocumentContent] = useState(false);
  const [suggestionState, setSuggestionState] =
    useState<EditorSuggestionState>(null);
  const [suggestionView, setSuggestionView] = useState<EditorView | null>(null);
  const handlersRef = useRef({
    setEditorValue,
    setFocusedField,
    setImmediateTextContent,
    setInlineImageUploading,
    setLastFocusedField,
    updateInlineImageMetadata,
  });
  handlersRef.current = {
    setEditorValue,
    setFocusedField,
    setImmediateTextContent,
    setInlineImageUploading,
    setLastFocusedField,
    updateInlineImageMetadata,
  };

  const setInlineImageUploadStateFromDoc = useCallback(
    (doc: ProseMirrorNode, serializedValue?: string) => {
      handlersRef.current.setInlineImageUploading(
        docHasUnresolvedInlineImageUpload(doc) ||
          markdownHasLocalBlobImage(
            serializedValue ?? serializeDocForMode(doc, modeRef.current),
          ),
      );
    },
    [],
  );

  const handleSuggestionChange = useCallback(
    (state: EditorSuggestionState, view: EditorView) => {
      setSuggestionState(state);
      setSuggestionView(state ? view : null);
    },
    [],
  );

  const handleSuggestionKeyDown = useCallback(
    (
      event: KeyboardEvent,
      state: NonNullable<EditorSuggestionState>,
      view: EditorView,
    ) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeEditorSuggestion(view);
        return true;
      }

      const controls = suggestionControlsRef.current;

      if (!controls || controls.optionCount() === 0) {
        return false;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        event.stopPropagation();
        controls.moveSelection(1);
        return true;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        event.stopPropagation();
        controls.moveSelection(-1);
        return true;
      }

      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        event.stopPropagation();

        if (!controls.pickSelected()) {
          closeEditorSuggestion(view);
        }

        return true;
      }

      return false;
    },
    [],
  );

  const lifecyclePlugin = useMemo(
    () =>
      new Plugin({
        props: {
          handleDOMEvents: {
            blur: () => {
              setFocused(false);
              handlersRef.current.setFocusedField(null);
              return false;
            },
            focus: () => {
              setFocused(true);
              handlersRef.current.setFocusedField(name);
              handlersRef.current.setLastFocusedField(name);
              return false;
            },
          },
        },
      }),
    [name],
  );

  const plugins = useMemo(
    () => [
      lifecyclePlugin,
      ...buildEditorPlugins({
        mode,
        suggestions: {
          onChange: handleSuggestionChange,
          onKeyDown: handleSuggestionKeyDown,
        },
        uploadImageFile,
      }),
    ],
    [
      handleSuggestionChange,
      handleSuggestionKeyDown,
      lifecyclePlugin,
      mode,
      placeholder,
      uploadImageFile,
    ],
  );

  useLayoutEffect(() => {
    if (!rootRef.current) {
      return;
    }

    const view = new EditorView(rootRef.current, {
      attributes: {
        class: "editor-v3-surface",
        [IGNORE_SAME_ORIGIN_LINK_CLICK_ATTRIBUTE]: "true",
      },
      dispatchTransaction(transaction) {
        const nextState = view.state.apply(transaction);
        view.updateState(nextState);

        if (transaction.docChanged) {
          const hasContent = docHasContent(nextState.doc);
          const serializedValue = serializeDocForMode(
            nextState.doc,
            modeRef.current,
          );

          rememberLocalBoundaryValue(
            localBoundaryValuesRef.current,
            serializedValue,
          );

          setHasDocumentContent(hasContent);
          handlersRef.current.setImmediateTextContent(hasContent);
          setInlineImageUploadStateFromDoc(nextState.doc, serializedValue);

          if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
          }

          debounceTimeout.current = setTimeout(() => {
            handlersRef.current.setEditorValue(serializedValue);
          }, 200);
        }
      },
      nodeViews: {
        code_block: createCodeBlockNodeView,
        image: createImageNodeViewFactory({
          updateImageMetadata(updater) {
            handlersRef.current.updateInlineImageMetadata(updater);
          },
        }),
      },
      state: EditorState.create({
        doc: parseValueToDoc(value, modeRef.current),
        plugins,
        schema: focusSchema,
      }),
    });

    viewRef.current = view;
    setEditorInstance({
      getter: () => view,
      type: "prosemirror",
    });
    const hasContent = docHasContent(view.state.doc);
    setHasDocumentContent(hasContent);
    setImmediateTextContent(hasContent);
    setInlineImageUploadStateFromDoc(view.state.doc);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      view.destroy();
      viewRef.current = null;
      handlersRef.current.setInlineImageUploading(false);
      setSuggestionState(null);
      setSuggestionView(null);
    };
  }, []);

  useEffect(() => {
    const view = viewRef.current;

    if (!view || modeRef.current === mode) {
      modeRef.current = mode;
      return;
    }

    const previousMode = modeRef.current;
    const markdown = serializeDocForMode(view.state.doc, previousMode);
    const nextDoc = parseValueToDoc(markdown, mode);

    modeRef.current = mode;
    view.dispatch(
      view.state.tr
        .replaceWith(0, view.state.doc.content.size, nextDoc.content)
        .setMeta(editorSuggestionsKey, { close: true }),
    );
  }, [mode]);

  useEffect(() => {
    const view = viewRef.current;

    if (!view) {
      return;
    }

    view.updateState(
      view.state.reconfigure({
        plugins,
      }),
    );
  }, [plugins]);

  useEffect(() => {
    const view = viewRef.current;

    if (!view) {
      return;
    }

    const currentMarkdown = serializeDocForMode(
      view.state.doc,
      modeRef.current,
    );
    const normalizedValue = normalizeBoundaryValue(value);

    if (normalizeBoundaryValue(currentMarkdown) === normalizedValue) {
      localBoundaryValuesRef.current.delete(normalizedValue);
      return;
    }

    if (localBoundaryValuesRef.current.delete(normalizedValue)) {
      return;
    }

    const nextDoc = parseValueToDoc(value, modeRef.current);
    view.dispatch(
      view.state.tr
        .replaceWith(0, view.state.doc.content.size, nextDoc.content)
        .setMeta(editorSuggestionsKey, { close: true }),
    );
  }, [value]);

  useEffect(() => {
    if (!autoFocus) {
      return;
    }

    const focusVisibleEditor = () => {
      const view = viewRef.current;

      if (!view || !isEditorBranchVisible(view.dom)) {
        return;
      }

      view.focus();
    };

    focusVisibleEditor();

    const frame = requestAnimationFrame(focusVisibleEditor);
    const timeout = setTimeout(focusVisibleEditor, 100);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timeout);
    };
  }, [autoFocus]);

  return (
    <div
      className={`editor-v3 relative h-full min-h-6 ${className}`}
      data-editor-container={true}
      data-editor-v3-field={name}
      data-editor-v3-mode={mode}
    >
      <div
        ref={rootRef}
        className={`
          custom-scrollbar h-full min-h-6 overflow-x-hidden overflow-y-auto
          ${mode === EDITOR_MODE.SIMPLE ? "editor-v3-markdown" : ""}
        `}
      />
      {placeholder && !hasDocumentContent && !focused && (
        <div className="pointer-events-none absolute inset-x-0 top-1 h-6 truncate text-sm/6 text-muted">
          {placeholder}
        </div>
      )}
      {suggestionState && suggestionView && (
        <EditorSuggestionMenu
          controlsRef={suggestionControlsRef}
          mode={mode}
          suggestion={suggestionState}
          view={suggestionView}
        />
      )}
    </div>
  );
};
